import { Icon } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { useId } from 'react';
import clsx from 'clsx';
import * as styles from './index.css';

/** SVG paths for the gradient hover layer only (default uses Pluma `Icon` sparkle). */
const SPARKLE_LARGE_PATH =
  'M15.987 9.36 23 12l-7.013 2.639L13.29 21.5l-2.699-6.861L3.58 12l7.012-2.64L13.29 2.5l2.697 6.86Z';
const SPARKLE_SMALL_BOTTOM_PATH = 'M5.189 19.068H3v-1.285h2.19V15.64h1.313v2.142h2.189v1.285h-2.19v2.142H5.19v-2.142Z';
const SPARKLE_SMALL_TOP_PATH = 'M5.19 6.217H3V4.932h2.19V2.79h1.313v2.142h2.189v1.285h-2.19V8.36H5.19V6.217Z';

/** Matches command-dialog "Ask Agent" row (`command-dialog.tsx`). */
const askAgentSparkleIconProps = {
  name: 'sparkle',
  size: 'sm',
  color: 'primary',
} as const;

type AskAgentSparkleIconProps = {
  className?: string;
  /**
   * 'crossfade' fades plain→gradient with the GradientRing; 'gradient' is
   * always colorful; 'inverse' fills with currentColor for colored surfaces.
   */
  variant?: 'crossfade' | 'gradient' | 'inverse';
};

/**
 * Sparkle that crossfades from the plain Pluma icon to an orange→purple
 * gradient while the surrounding `GradientRing` is hovered or focused, or
 * renders permanently colorful (`variant="gradient"`) or solid white for
 * gradient surfaces (`variant="inverse"`).
 */
export function AskAgentSparkleIcon({ className, variant = 'crossfade' }: AskAgentSparkleIconProps) {
  const id = useId();
  const gradientId = `ask-agent-sparkle-gradient-${id.replace(/:/g, '')}`;
  // 'inverse' inherits the surrounding control's color (theme-aware contrast).
  const fill = variant === 'inverse' ? 'currentColor' : `url(#${gradientId})`;

  return (
    <span className={clsx(styles.sparkleWrap, className)} aria-hidden>
      {variant === 'crossfade' ? (
        <span className={styles.sparklePluma}>
          <Icon {...askAgentSparkleIconProps} />
        </span>
      ) : null}
      <svg
        className={variant === 'crossfade' ? styles.sparkleGradient : styles.sparkleGradientAlways}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {variant === 'inverse' ? null : (
          <defs>
            <linearGradient id={gradientId} x1="100%" y1="0%" x2="0%" y2="0%">
              <stop offset="0%" style={{ stopColor: themeVars.palette['orange-500'] }} />
              <stop offset="100%" style={{ stopColor: themeVars.palette['violet-600'] }} />
            </linearGradient>
          </defs>
        )}
        <path d={SPARKLE_LARGE_PATH} fill={fill} />
        <path d={SPARKLE_SMALL_TOP_PATH} fill={fill} />
        <path d={SPARKLE_SMALL_BOTTOM_PATH} fill={fill} />
      </svg>
    </span>
  );
}
